import localforage from 'localforage';
import firebase from 'firebase/app';
import axios from 'axios';
import moment from 'moment';
import { getSafeToken } from './firebaseTokenHelper';
import { registerStore } from './offlineStoreRegistry';
import { safeSetItem } from './storageQuotaGuard';
import { encodePhotoBinary, decodePhotoBinary } from './fieldPhotoBinary';

export const FIELD_PHOTO_STORE = 'field_photos';
export const photoStore = localforage.createInstance({ name: 'FieldPhotos', storeName: 'photos', driver: localforage.INDEXEDDB });
const sessionStore = localforage.createInstance({ name: 'FieldPhotos', storeName: 'sessions', driver: localforage.INDEXEDDB });
const queueStore = localforage.createInstance({ name: 'OfflineOperations_field_photos' });
const gpsStore = localforage.createInstance({ name: 'backgroundGPS', storeName: 'coords' });
const tagStore = localforage.createInstance({ name: 'FieldPhotos', storeName: 'tags' });
const clientId = process.env.REACT_APP_CLIENT || 'default';
let writes = Promise.resolve();
export const photoScope = (uid) => `${clientId}:${uid}`;
export const newPhotoId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
const serialize = (action) => {
  const result = writes.catch(() => {}).then(() => navigator.locks?.request ? navigator.locks.request('msales-field-photos', action) : action());
  writes = result;
  return result;
};
const stored = async (store, key, value) => {
  if (store === photoStore) {
    value = { ...value, blob: await encodePhotoBinary(value.blob), thumbnail: await encodePhotoBinary(value.thumbnail) };
  }
  if (!await safeSetItem(store, key, value)) throw new Error('Fotografija nije spremljena: memorija uređaja je puna.');
};
const announce = () => window.dispatchEvent(new Event('field-photos-changed'));

export const fieldPhotoRequest = async (method, path, data) => {
  const user = firebase.auth().currentUser;
  const token = await getSafeToken(user, 5000, false);
  if (!token) throw new Error('Prijavite se ponovo prije slanja fotografija.');
  const base = (process.env.REACT_APP_API_URL || process.env.REACT_APP_GRAPHQL_SERVER?.replace(/\/graphql\/?$/, '') || '').replace(/\/+$/, '');
  try {
    return (await axios({ method, url: `${base}/api/field-photos${path}`, data, headers: { Authorization: `Bearer ${token}` }, timeout: 150000 })).data;
  } catch (error) {
    const safeError = new Error(error.response?.data?.error || error.message || 'Slanje fotografije nije uspjelo.');
    safeError.statusCode = error.response?.status;
    safeError.code = error.code;
    throw safeError;
  }
};

export const getCachedPhotoTags = () => tagStore.getItem(clientId);
export const refreshPhotoTags = async (force = false) => {
  const cachedAt = await tagStore.getItem(`${clientId}:updatedAt`);
  if (!force && cachedAt && Date.now() - cachedAt < 60 * 60 * 1000) return getCachedPhotoTags();
  const { nodes } = await fieldPhotoRequest('GET', '/tags');
  if (!Array.isArray(nodes)) throw new Error('Šifrarnik tagova nije dostupan.');
  await stored(tagStore, clientId, nodes);
  await stored(tagStore, `${clientId}:updatedAt`, Date.now());
  return nodes;
};

export const getPhotoSession = (uid, customer, startTime) => serialize(async () => {
  const started = moment(startTime, [moment.ISO_8601, 'DD-MM-YYYY HH:mm:ss'], true);
  const startedAt = started.isValid() ? started.toISOString() : null;
  const key = `${photoScope(uid)}:${customer.id}:${startedAt || 'restored'}`;
  let session = await sessionStore.getItem(key);
  if (!session || session.endedAt) {
    session = { key, id: newPhotoId(), scope: photoScope(uid), customerId: String(customer.id), startedAt: startedAt || new Date().toISOString(), photoKeys: [], lastPhoto: null };
    await stored(sessionStore, key, session);
  }
  return session;
});

const enqueue = async (operation) => {
  const operations = (await queueStore.getItem('operations')) || [];
  if (!operations.some((entry) => entry.id === operation.id)) await stored(queueStore, 'operations', [...operations, operation]);
  await registerStore(FIELD_PHOTO_STORE, '/customer-action');
};
export const enqueueFieldPhotoOperation = (operation) => serialize(async () => { await enqueue(operation); announce(); });
export const removeQueuedFieldPhotoOperations = (ids) => serialize(async () => {
  const removed = new Set(ids);
  const operations = (await queueStore.getItem('operations')) || [];
  await stored(queueStore, 'operations', operations.filter((operation) => !removed.has(operation.id)));
});
const uploadOperation = (record) => ({
  id: `field_photo_${record.metadata.photoId}`, type: 'UPLOAD_FIELD_PHOTO', variables: { photoKey: record.key, ownerUid: record.ownerUid },
  createdAt: record.metadata.capturedAt, customer: record.customer,
});
const contextOperation = (record) => ({
  id: `field_photo_context_${record.metadata.photoId}_${record.contextRevision}`, type: 'UPDATE_FIELD_PHOTO_CONTEXT',
  variables: { photoKey: record.key, ownerUid: record.ownerUid }, createdAt: new Date().toISOString(), customer: record.customer,
});

export const saveFieldPhoto = (uid, session, customer, blob, thumbnail, metadata) => serialize(async () => {
  const currentSession = (await sessionStore.getItem(session.key)) || session;
  if (currentSession.endedAt) throw new Error('Posjeta je završena. Otvorite kupca i fotografišite ponovo.');
  const existing = await photoStore.getItem(`${photoScope(uid)}:${metadata.photoId}`);
  if (existing) {
    if (!currentSession.photoKeys.includes(existing.key)) {
      currentSession.photoKeys.push(existing.key);
      await stored(sessionStore, session.key, currentSession);
    }
    if (existing.status !== 'sent' && existing.status !== 'cancelled') await enqueue(uploadOperation(existing));
    announce(); return existing;
  }
  const previous = currentSession.lastPhoto;
  const seconds = previous ? Math.round((Date.parse(metadata.capturedAt) - Date.parse(previous.capturedAt)) / 1000) : null;
  const record = {
    key: `${photoScope(uid)}:${metadata.photoId}`, scope: photoScope(uid), ownerUid: uid,
    customer: { id: customer.id, code: customer.code, name: customer.name },
    blob, thumbnail, status: 'pending', contextRevision: 0, syncedContextRevision: 0,
    metadata: {
      ...metadata, customerId: String(customer.id), sessionId: currentSession.id,
      visitStartedAt: currentSession.startedAt,
      elapsedSeconds: Math.max(0, Math.round((Date.parse(metadata.capturedAt) - Date.parse(currentSession.startedAt)) / 1000)),
      previousPhotoId: previous?.photoId || null, secondsSincePrevious: seconds >= 0 ? seconds : null,
    },
    context: { ...(currentSession.orderId ? { orderId: currentSession.orderId } : {}), ...(currentSession.orderReference ? { orderReference: currentSession.orderReference } : {}) },
  };
  await stored(photoStore, record.key, record);
  currentSession.photoKeys = [...new Set([...currentSession.photoKeys, record.key])];
  currentSession.lastPhoto = { photoId: metadata.photoId, capturedAt: metadata.capturedAt };
  await stored(sessionStore, session.key, currentSession);
  await enqueue(uploadOperation(record));
  announce();
  return record;
});

export const getSessionPhotos = async (session) => {
  if (!session) return [];
  const current = (await sessionStore.getItem(session.key)) || session;
  const photos = [];
  for (const key of current.photoKeys) {
    const photo = await photoStore.getItem(key);
    if (photo) photos.push({ key, metadata: photo.metadata, thumbnail: decodePhotoBinary(photo.thumbnail), status: photo.status, lastError: photo.lastError });
  }
  return photos;
};

export const getPhotoGps = async () => {
  const gps = await gpsStore.getItem('lastCoords');
  return gps ? {
    latitude: gps.coords.latitude, longitude: gps.coords.longitude, gpsAccuracy: gps.coords.accuracy,
    gpsObservedAt: new Date(gps.timestamp).toISOString(),
  } : { latitude: null, longitude: null, gpsAccuracy: null, gpsObservedAt: null };
};

export const updatePhotoSessionContext = (session, context) => serialize(async () => {
  if (!session) return;
  const current = (await sessionStore.getItem(session.key)) || session;
  if (context.visitEndedAt) current.endedAt = current.endedAt || context.visitEndedAt;
  if (context.orderId) current.orderId = String(context.orderId);
  if (context.orderReference) current.orderReference = context.orderReference;
  await stored(sessionStore, current.key, current);
  const patch = { ...context, ...(context.visitEndedAt ? { visitEndedAt: current.endedAt } : {}) };
  for (const key of current.photoKeys) {
    const record = await photoStore.getItem(key);
    if (!record || record.status === 'cancelled' || !Object.keys(patch).some((field) => patch[field] !== record.context[field])) continue;
    record.context = { ...record.context, ...patch };
    record.contextRevision++;
    await stored(photoStore, key, record);
    await enqueue(contextOperation(record));
  }
  announce();
});

export const associatePhotoOrder = async (sessionId, orderId, orderReference) => {
  if (!sessionId || !orderId) return;
  const uid = firebase.auth().currentUser?.uid;
  const keys = await sessionStore.keys();
  for (const key of keys) {
    const session = await sessionStore.getItem(key);
    if (session.id === sessionId && session.scope === photoScope(uid)) {
      await updatePhotoSessionContext(session, { orderId: String(orderId), orderReference: orderReference || null });
      return;
    }
  }
};

export const recoverFieldPhotos = (uid) => serialize(async () => {
  const failedStore = localforage.createInstance({ name: 'FailedOperations_field_photos' });
  const failedOperations = (await failedStore.getItem('operations')) || [];
  const failedIds = new Set(failedOperations.map((operation) => operation.id));
  const sessions = [];
  for (const key of (await sessionStore.keys()).filter((key) => key.startsWith(`${photoScope(uid)}:`))) {
    const session = await sessionStore.getItem(key);
    if (session) sessions.push(session);
  }
  const keys = (await photoStore.keys()).filter((key) => key.startsWith(`${photoScope(uid)}:`));
  for (const key of keys) {
    const record = await photoStore.getItem(key);
    if (record.status === 'cancelled') continue;
    const session = sessions.find((entry) => entry.id === record.metadata.sessionId);
    if (session) {
      if (!session.photoKeys.includes(key)) { session.photoKeys.push(key); await stored(sessionStore, session.key, session); }
      const context = { ...(session.endedAt ? { visitEndedAt: session.endedAt } : {}), ...(session.orderId ? { orderId: session.orderId } : {}), ...(session.orderReference ? { orderReference: session.orderReference } : {}) };
      if (Object.keys(context).some((field) => context[field] !== record.context[field])) {
        record.context = { ...record.context, ...context }; record.contextRevision++;
        await stored(photoStore, key, record);
      }
    }
    const upload = uploadOperation(record), context = contextOperation(record);
    if (record.status !== 'sent' && !failedIds.has(upload.id)) await enqueue(upload);
    if (record.contextRevision > record.syncedContextRevision && !failedIds.has(context.id) && !failedIds.has(upload.id)) await enqueue(context);
  }
  const protectedKeys = new Set(failedOperations.map((operation) => operation.variables?.photoKey));
  for (const queueKey of ['operations', 'sync_backup', 'orphaned_ops']) {
    for (const operation of (await queueStore.getItem(queueKey)) || []) protectedKeys.add(operation.variables?.photoKey);
  }
  for (const session of sessions) {
    if (!session.endedAt || Date.now() - Date.parse(session.endedAt) < 7 * 24 * 60 * 60 * 1000) continue;
    if (session.photoKeys.some((key) => protectedKeys.has(key))) continue;
    let complete = true;
    for (const key of session.photoKeys) {
      const record = await photoStore.getItem(key);
      if (record && ((record.status !== 'sent' && record.status !== 'cancelled') || record.contextRevision > record.syncedContextRevision)) { complete = false; break; }
    }
    if (complete) {
      for (const key of session.photoKeys) await photoStore.removeItem(key);
      await sessionStore.removeItem(session.key);
    }
  }
  announce();
});

export const discardFieldPhotoOperations = (operations) => serialize(async () => {
  const removedKeys = new Set();
  for (const operation of operations) {
    const key = operation.variables?.photoKey;
    if (!key) continue;
    const record = await photoStore.getItem(key);
    if (!record) continue;
    removedKeys.add(key);
    await stored(photoStore, key, { ...record, status: record.status === 'sent' ? 'sent' : 'cancelled', blob: null, syncedContextRevision: record.contextRevision });
  }
  const pending = (await queueStore.getItem('operations')) || [];
  await stored(queueStore, 'operations', pending.filter((operation) => !removedKeys.has(operation.variables?.photoKey)));
  announce();
});

const ownedPhoto = async ({ photoKey, ownerUid }) => {
  if (firebase.auth().currentUser?.uid !== ownerUid) throw new Error('Fotografija pripada drugom korisniku. Prijavite se odgovarajućim nalogom.');
  const record = await photoStore.getItem(photoKey);
  if (!record || record.ownerUid !== ownerUid) throw new Error('Lokalna fotografija nije pronađena.');
  return record;
};

export const syncFieldPhotoUpload = async (variables) => {
  const record = await ownedPhoto(variables);
  if (record.status === 'sent' || record.status === 'cancelled') return;
  if (!record.blob) throw new Error('Datoteka fotografije nije pronađena.');
  const form = new FormData();
  form.append('metadata', JSON.stringify({ ...record.metadata, ...record.context }));
  form.append('file', decodePhotoBinary(record.blob), `${record.metadata.photoId}.jpg`);
  let result;
  try {
    result = await fieldPhotoRequest('POST', '', form);
    if (!result.success || result.status !== 'ready' || result.photoId !== record.metadata.photoId) throw new Error('Server nije potvrdio spremanje fotografije.');
  } catch (error) {
    await serialize(async () => {
      const fresh = await ownedPhoto(variables);
      if (fresh.status !== 'cancelled') await stored(photoStore, fresh.key, { ...fresh, status: 'failed', lastError: error.message });
    }).catch(() => {});
    announce();
    throw error;
  }
  await serialize(async () => {
    const fresh = await ownedPhoto(variables);
    if (fresh.status !== 'cancelled') await stored(photoStore, fresh.key, { ...fresh, blob: null, status: 'sent', lastError: null, serverId: result.id });
  });
  announce();
};

export const syncFieldPhotoContext = async (variables) => {
  const record = await ownedPhoto(variables);
  if (record.status === 'cancelled') return;
  if (record.contextRevision <= record.syncedContextRevision) return;
  await syncFieldPhotoUpload(variables);
  const revision = record.contextRevision;
  await fieldPhotoRequest('PATCH', `/${record.metadata.photoId}/context`, record.context);
  await serialize(async () => {
    const fresh = await ownedPhoto(variables);
    await stored(photoStore, fresh.key, { ...fresh, syncedContextRevision: Math.max(fresh.syncedContextRevision, revision) });
  });
};
